// Shipment Control (as v2.0.41): allocating Ready to Ship PO lines to shipments (all or part, by size, metres or
// quantity), the shipment status of each line for the Time & Action Plan, and when a line counts as received. Shared by
// the server (which enforces it) and the screens (which show available quantities while typing).

export const SHIPMENT_STATUSES = [["loading", "Loading"], ["in_transit", "In Transit"], ["landed", "Landed"], ["received", "Received"]] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number][0];
export const SHIPMENT_STATUS_LABEL = Object.fromEntries(SHIPMENT_STATUSES) as Record<ShipmentStatus, string>;
export const SHIPMENT_METHODS = ["Air", "Sea"] as const;
/** Shipping documents kept with a shipment (PDF). The Bill of Lading is uploaded from the shipment itself. */
export const SHIPMENT_DOCUMENTS = [["bol", "Bill of Lading (BOL)"], ["commercialInvoice", "Commercial Invoice"], ["certificateOfOrigin", "Certificate of Origin"],
  ["customsDocument", "Export / Customs Document"], ["otherDocument", "Other Shipping Document"]] as const;
export type ShipmentDocKind = (typeof SHIPMENT_DOCUMENTS)[number][0];

/** What the allocation of one PO line needs to know about the line. */
export interface ShipLine { itemType: string; sizeSet: string[]; qtyBySize: Record<string, number>; quantity: number }
export interface Qty { qtyBySize: Record<string, number>; total: number }

/** Garments with sizes are allocated per size (whole pieces); fabric in metres (2 decimals); anything else by quantity. */
export const bySize = (l: ShipLine) => l.itemType === "product" && l.sizeSet.length > 0;
export const unitLabel = (l: Pick<ShipLine, "itemType">) => (l.itemType === "fabric" ? "m" : "pcs");
const round2 = (n: number) => Math.round(n * 100) / 100;

export function normalizeQty(l: ShipLine, x: { qtyBySize?: Record<string, unknown>; total?: unknown } = {}): Qty {
  if (bySize(l)) {
    const qtyBySize = Object.fromEntries(l.sizeSet.map((s) => [s, Math.max(0, Math.floor(Number(x.qtyBySize?.[s]) || 0))]));
    return { qtyBySize, total: Object.values(qtyBySize).reduce((a, b) => a + b, 0) };
  }
  let total = Math.max(0, Number(x.total) || 0);
  total = l.itemType === "fabric" ? round2(total) : Math.floor(total);
  return { qtyBySize: {}, total };
}
export const orderedQty = (l: ShipLine): Qty => normalizeQty(l, { qtyBySize: l.qtyBySize, total: l.quantity });

function sum(l: ShipLine, parts: Qty[]): Qty {
  if (bySize(l)) {
    const qtyBySize = Object.fromEntries(l.sizeSet.map((s) => [s, parts.reduce((a, p) => a + (Number(p.qtyBySize[s]) || 0), 0)]));
    return { qtyBySize, total: Object.values(qtyBySize).reduce((a, b) => a + b, 0) };
  }
  return { qtyBySize: {}, total: round2(parts.reduce((a, p) => a + (Number(p.total) || 0), 0)) };
}
/** Ordered minus what other shipments carry and what was closed as a factory shortfall. */
export function remainingQty(l: ShipLine, allocatedElsewhere: Qty[], closed: Qty[]): Qty {
  const ord = orderedQty(l), used = sum(l, [...allocatedElsewhere, ...closed]);
  if (bySize(l)) {
    const qtyBySize = Object.fromEntries(l.sizeSet.map((s) => [s, Math.max(0, (ord.qtyBySize[s] || 0) - (used.qtyBySize[s] || 0))]));
    return { qtyBySize, total: Object.values(qtyBySize).reduce((a, b) => a + b, 0) };
  }
  return { qtyBySize: {}, total: Math.max(0, round2(ord.total - used.total)) };
}
export const totalOf = (l: ShipLine, parts: Qty[]) => sum(l, parts);

/** The first size (or the line) where `want` is more than `available`, or null. */
export function overAllocated(l: ShipLine, want: Qty, available: Qty): string | null {
  if (bySize(l)) return l.sizeSet.find((s) => (want.qtyBySize[s] || 0) > (available.qtyBySize[s] || 0)) ?? null;
  return want.total > available.total + 0.0001 ? "" : null;
}

/**
 * Shipping more than the PO (approved 01/11/2026): a shipment may carry more than is left on the PO (the factory made
 * extra, the customer asked for more) when a reason is given. The PO quantity is never changed; the extra is shown as
 * "over PO" and logged. Shipping less was always allowed (the balance stays open or is closed as a factory shortfall).
 */
export const OVER_REASONS = ["Factory made extra", "Customer asked for more", "Other"] as const;
export type OverReason = (typeof OVER_REASONS)[number];
/** How much of `want` is more than `available` (per size for garments with sizes). */
export function overBy(l: ShipLine, want: Qty, available: Qty): Qty {
  if (bySize(l)) {
    const qtyBySize = Object.fromEntries(l.sizeSet.map((s) => [s, Math.max(0, (want.qtyBySize[s] || 0) - (available.qtyBySize[s] || 0))]));
    return { qtyBySize, total: Object.values(qtyBySize).reduce((a, b) => a + b, 0) };
  }
  return { qtyBySize: {}, total: Math.max(0, round2(want.total - available.total)) };
}
/** "S +10, M +20" — the sizes that are over (garments with sizes), for messages and the Activity Log. */
export const overSizesText = (l: ShipLine, over: Qty) => (bySize(l) ? l.sizeSet.filter((s) => over.qtyBySize[s] > 0).map((s) => `${s} +${over.qtyBySize[s]}`).join(", ") : "");

/** A line's shipment status for the Time & Action Plan (v2.0.41 shipmentControlStatusForLine). */
export function lineShipmentStatus(statuses: ShipmentStatus[], fullyAllocated: boolean): string {
  if (!statuses.length) return "Loading";
  const all = (s: ShipmentStatus) => statuses.every((x) => x === s);
  const any = (...s: ShipmentStatus[]) => statuses.some((x) => s.includes(x));
  if (all("received")) return "Received";
  if (any("received")) return "Part Received";
  if (fullyAllocated && all("landed")) return "Landed";
  if (!fullyAllocated && any("in_transit", "landed")) return "Part Shipped";
  if (fullyAllocated && any("loading") && !all("loading")) return "Part Shipped";
  if (any("in_transit", "landed")) return "In Transit";
  return "Loading";
}

/**
 * A line is received once every shipment carrying it is received (v2.0.41): a delivered shipment completes the line
 * even when the factory shipped less; any balance stays available for another shipment or is closed as a shortfall.
 */
export function lineReceipt(shipments: Array<{ status: ShipmentStatus; receivedAt: string | null; receivedBy: string }>) {
  const received = shipments.filter((s) => s.status === "received");
  const dates = received.map((s) => String(s.receivedAt || "").slice(0, 10)).filter(Boolean).sort();
  const last = received.slice().sort((a, b) => String(a.receivedAt).localeCompare(String(b.receivedAt))).pop();
  return {
    status: shipments.length && received.length === shipments.length ? "Received" : received.length ? "Part Received" : "",
    date: dates[dates.length - 1] || "", by: last?.receivedBy || "",
  };
}

export const shipmentLabel = (s: { consolidated: boolean; number: number }) => (s.consolidated ? `Consolidated C${String(s.number).padStart(3, "0")}` : `Shipment ${s.number}`);
